import { cfiRangeFromCfiPair } from "../CfiUtils";
import { eq } from "../CommonUtils";

export default class Note {
  /**
   * @param {number} bookId Bookshare book ID
   * @param {string} location Range of note, in CFI
   * @param {string} text Text excerpt
   * @param {string} color Note color in hex format
   * @param {string} note User-entered note
   * @param {*} locator Readium-compatible locator
   * @param {string} dateUpdated Date as an ISO string
   * @param {boolean} synced Indicates whether note has been saved with a remote service.
   * @param {boolean} shouldDelete Indicates whether the note has been marked for deletion.
   */
  constructor(
    bookId,
    location,
    text,
    color,
    note,
    locator = {},
    dateUpdated = null,
    synced = false,
    shouldDelete = false,
  ) {
    /** @type {Number} Bookshare title instance ID */
    this.bookId = bookId;

    /** @type {string} Location of note, expressed as a CFI range */
    this.location = location;

    /** @type {string} Textual content of note */
    this.text = text;

    /** @type {string} Note color in hex format */
    this.color = color;

    /** @type {string} User-entered note */
    this.note = note;

    /** @type {object} Additional locator data */
    this.locator = locator;

    /** @type {boolean} True if previously synced to the server */
    this.synced = synced;

    /** @type {boolean} True if note is pending server deletion */
    this.shouldDelete = shouldDelete;

    /** @type {Date} Date created */
    const dateInMilliseconds = Date.parse(dateUpdated);
    this.dateUpdated = isNaN(dateInMilliseconds)
      ? new Date()
      : new Date(dateInMilliseconds);
  }

  equals(other) {
    return (
      this.bookId === other.bookId &&
      this.location === other.location &&
      this.text === other.text &&
      this.color === other.color &&
      this.note === other.note &&
      eq(this.locator, other.locator) &&
      this.synced === other.synced &&
      this.shouldDelete === other.shouldDelete &&
      this.dateUpdated.getTime() === other.dateUpdated.getTime()
    );
  }

  /**
   * Creates a new Note from a JSON string.
   * @param {string} jsonString
   */
  static fromJsonString(jsonString) {
    return Note.fromJson(JSON.parse(jsonString));
  }

  /**
   * Creates a new Note from a JSON object.
   * @param {*} json
   */
  static fromJson(json) {
    // If we have start/end location attributes, generate a range CFI
    const location =
      json.hasOwnProperty("startLocation") && json.hasOwnProperty("endLocation")
        ? cfiRangeFromCfiPair(json.startLocation, json.endLocation)
        : json.location;

    return new Note(
      json.bookId || json.bookshareId,
      location,
      json.text,
      json.color,
      json.note,
      json.locator,
      json.dateUpdated,
      json.synced,
      json.shouldDelete,
    );
  }
}
